/* eslint-disable @typescript-eslint/no-empty-function */
import { arrayMove } from "@dnd-kit/sortable";
import { useLazyLoadQuery } from "@nova/react";
import * as React from "react";
import type { AppBarControlQuery } from "./__generated__/AppBarControlQuery.types.graphql";
import type { AppBarQuery } from "./__generated__/AppBarQuery.types.graphql";
import type { FlyoutQuickAccessQuery } from "./__generated__/FlyoutQuickAccessQuery.types.graphql";
import type { FlyoutSearchResultsQuery } from "./__generated__/FlyoutSearchResultsQuery.types.graphql";
import { controlModelQuery, dataQuery } from "./AppBar.types";
import { AppBarContextType, AppBarItem } from "./AppBarContext.types";
export type M365ApplicationEdge =
  AppBarQuery["response"]["m365Apps"]["edges"][number];
export type M365ApplicationAcquisitionLinks =
  AppBarQuery["response"]["m365Apps"]["appAcquisitionLinks"];
export type SuggestedM365ApplicationEdge =
  FlyoutSearchResultsQuery["response"]["m365Apps"]["edges"][number];
export type SuggestedM365LinkEdge =
  FlyoutQuickAccessQuery["response"]["m365Links"]["edges"][number];
export type AppBarControlModel =
  AppBarControlQuery["response"]["views"]["appBarControl"];
/**
 * Default function implementation for functions that should be overridden
 */
const NotImplemented = (): never => {
  throw new Error(
    "Function not implemented. Use the useAppBarContext hook to utilize AppBar context methods"
  );
};
/**
 * Initial context stub object
 */
export const AppBarInitialContext: AppBarContextType = {
  overflowIndex: undefined,
  maxLockedIndex: undefined,
  maxPinnedIndex: undefined,
  appBarData: undefined,
  hoistedItem: undefined,
  lockedItems: undefined,
  pinnedItems: undefined,
  overflowItems: undefined,
  anchoredItems: undefined,
  allAppBarItems: undefined,
  setLockedItems: NotImplemented,
  setPinnedItems: NotImplemented,
  setHoistedItem: NotImplemented,
  setOverflowIndex: NotImplemented,
  setAnchoredItems: NotImplemented,
  movePinnedItem: NotImplemented,
};
/**
 * Context object containing state data and methods to act on that data
 */
const AppBarContext =
  React.createContext<AppBarContextType>(AppBarInitialContext);
/**
 * Provides an AppBarContext implementation containing state for visible, hoisted and flyout AppBar items
 */
const AppBarContextProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const controlModelDataQuery = useLazyLoadQuery<AppBarControlQuery>(
    controlModelQuery,
    {}
  );
  const appBarDataQuery = useLazyLoadQuery<AppBarQuery>(dataQuery, {});
  const [overflowIndex, setOverflowIndex] = React.useState<
    number | undefined
  >();
  const [selectedItem, setSelectedItem] = React.useState<AppBarItem>();
  const [lockedItems, setLockedItems] = React.useState<AppBarItem[]>([]);
  const [pinnedItems, setPinnedItems] = React.useState<AppBarItem[]>([]);
  const [hoistedItem, setHoistedItem] = React.useState<AppBarItem>();
  const [overflowItems, setOverflowItems] = React.useState<AppBarItem[]>([]);
  const [anchoredItems, setAnchoredItems] = React.useState<AppBarItem[]>([]);
  const [allAppBarItems, setAllAppBarItems] = React.useState<AppBarItem[]>([]);
  const { maxLockedIndex, maxPinnedIndex } = React.useMemo(() => {
    return (
      appBarDataQuery.data?.m365Apps?.edges?.reduce<{
        maxLockedIndex: number;
        maxPinnedIndex: number;
      }>(
        ({ maxLockedIndex, maxPinnedIndex }, edge) => {
          if (
            edge?.node?.lockedIndex != null &&
            edge.node.lockedIndex > maxLockedIndex
          ) {
            maxLockedIndex = edge.node.lockedIndex;
          }
          if (
            edge?.node?.pinnedIndex != null &&
            edge.node.pinnedIndex > maxPinnedIndex
          ) {
            maxPinnedIndex = edge.node.pinnedIndex;
          }
          return { maxLockedIndex, maxPinnedIndex };
        },
        { maxLockedIndex: -1, maxPinnedIndex: -1 }
      ) || { maxLockedIndex: -1, maxPinnedIndex: -1 }
    );
  }, [appBarDataQuery.data]);
  /**
   * Wraps a GQL edge in an AppBarItem
   * @param item Edge object to wrap
   * @returns AppBarItem with contained edge data
   */
  const wrapNewItem = (item: M365ApplicationEdge): AppBarItem => {
    const newItem: AppBarItem = {
      data: {
        node: Object.assign({}, item.node),
      },
      elementRef: React.createRef(),
      itemRef: React.createRef(),
    };
    if (newItem.data.node.isInUse) {
      setSelectedItem(newItem);
    }
    return newItem;
  };
  /**
   * Sorted tile data specified by GQL AppBar data.
   * Sort order will be:  locked tiles > pinned tiles > rest of the tiles
   */
  const appBarData = React.useMemo(() => {
    if (appBarDataQuery.error) {
      throw appBarDataQuery.error;
    } else if (!appBarDataQuery?.data) {
      return undefined;
    }
    return appBarDataQuery.data;
  }, [appBarDataQuery.data, appBarDataQuery.error]);
  /**
   * All apps as defined in GQL
   */
  const allApps = React.useMemo(() => {
    const getEffectiveIndex = (edge: M365ApplicationEdge) => {
      if (edge?.node?.lockedIndex != null && edge.node.lockedIndex >= 0) {
        // If the node has a locked index specified, its value is its effective index
        return edge.node.lockedIndex;
      } else if (
        edge?.node?.pinnedIndex != null &&
        edge.node.pinnedIndex >= 0
      ) {
        // If the node is pinned, its value incremented by the max lock index is the effective index
        return maxLockedIndex + 1 + edge.node.pinnedIndex;
      } else {
        // All other nodes go to the end
        return Number.MAX_VALUE;
      }
    };
    return appBarData?.m365Apps?.edges
      .slice() // Shallow copy of readonly array
      .sort((edge1: M365ApplicationEdge, edge2: M365ApplicationEdge) => {
        // For sorting purposes, nodes where effective index is not >= 0 are moved to the back of the array
        const effectiveIndex1 = getEffectiveIndex(edge1);
        const effectiveIndex2 = getEffectiveIndex(edge2);
        // Sort by effective index first, then by name based on the prop value
        const shouldOverrideNameSorting =
          controlModelDataQuery?.data?.views?.appBarControl?.tileBehavior
            ?.overrideAppBarDefaultSort;
        if (shouldOverrideNameSorting) {
          return effectiveIndex1 - effectiveIndex2;
        } else {
          return (
            effectiveIndex1 - effectiveIndex2 ||
            edge1.node.name?.localeCompare(edge2.node.name)
          );
        }
      });
  }, [
    appBarData?.m365Apps?.edges,
    maxLockedIndex,
    controlModelDataQuery?.data?.views?.appBarControl?.tileBehavior
      ?.overrideAppBarDefaultSort,
  ]);
  /**
   * Visible tiles defined as allApps split at the overflow index
   */
  const lockedTiles = React.useMemo(() => {
    return allApps?.filter(
      (tile: M365ApplicationEdge) =>
        // Only show tiles with a lockedIndex or pinnedIndex in the AppBar
        tile.node.lockedIndex != null && tile.node.lockedIndex >= 0
    );
  }, [allApps]);
  /**
   * Visible tiles defined as allApps split at the overflow index
   */
  const pinnedTiles = React.useMemo(() => {
    const includeLockedTiles =
      !controlModelDataQuery?.data?.views?.appBarControl?.tileBehavior
        ?.shouldRemoveLockedAppsFromPinnedApps;
    return allApps?.filter((tile: M365ApplicationEdge) => {
      const includeTileWithPinnedIndex =
        tile.node.pinnedIndex != null && tile.node.pinnedIndex >= 0;
      const excludeTileWithLockedIndex =
        tile.node.lockedIndex == null ||
        tile.node.lockedIndex === undefined ||
        tile.node.lockedIndex < 0;
      // if includeLockedTiles is true, include all tiles with pinnedIndex irrespective of lockedIndex
      // if includeLockedTiles is false, include only tiles with pinnedIndex and no lockedIndex
      return (
        includeTileWithPinnedIndex &&
        (includeLockedTiles || excludeTileWithLockedIndex)
      );
    });
  }, [
    allApps,
    controlModelDataQuery?.data?.views?.appBarControl?.tileBehavior
      ?.shouldRemoveLockedAppsFromPinnedApps,
  ]);
  /**
   * Visible tiles defined as allApps that are anchored to the bottom of the App Bar.
   */
  const anchoredTiles = React.useMemo(() => {
    return allApps
      ?.filter((tile: M365ApplicationEdge) => {
        return tile.node.anchoredIndex != null && tile.node.anchoredIndex >= 0;
      })
      .sort((edge1: M365ApplicationEdge, edge2: M365ApplicationEdge) => {
        const edge1AnchoredIndex = edge1.node.anchoredIndex || 0;
        const edge2AnchoredIndex = edge2.node.anchoredIndex || 0;
        return edge1AnchoredIndex - edge2AnchoredIndex;
      });
  }, [allApps]);
  /**
   * Remaining tiles which are not visible and displayed in overflow
   */
  const overflowTiles = React.useMemo(() => {
    if (overflowIndex != null) {
      return allApps
        ?.slice(overflowIndex)
        .filter((tile: M365ApplicationEdge) => {
          return !tile.node.isInUse && tile.node.anchoredIndex == null;
        });
    } else {
      return [];
    }
  }, [allApps, overflowIndex]);
  /**
   * Returns the index in allApps that is currently selected by the user
   */
  const selectedTileIndex = React.useMemo(() => {
    const selectedIndex =
      allApps?.findIndex((elem: M365ApplicationEdge) => {
        return !!elem.node.isInUse;
      }) ?? -1;
    if (selectedItem && selectedIndex === -1) {
      setSelectedItem(undefined);
    }
    return selectedIndex;
  }, [allApps, selectedItem]);
  /**
   * Tile in overflow that is currently in use and hoisted to the AppBar
   */
  React.useEffect(() => {
    if (!allApps) {
      return;
    }
    if (
      overflowIndex !== undefined &&
      selectedTileIndex !== undefined &&
      selectedTileIndex >= overflowIndex &&
      allApps[selectedTileIndex].node.anchoredIndex == null
    ) {
      setHoistedItem(wrapNewItem(allApps[selectedTileIndex]));
    } else {
      setHoistedItem(undefined);
    }
  }, [allApps, overflowIndex, selectedTileIndex]);

  const movePinnedItem = React.useCallback(
    (id: string, index: number) => {
      const newPinnedItems: AppBarItem[] = [];
      pinnedItems.map((item: AppBarItem) => {
        newPinnedItems.push(item);
      });
      if (hoistedItem && id === hoistedItem.data.node.id) {
        if (hoistedItem.data.node.pinnedIndex === undefined) {
          newPinnedItems.push(hoistedItem);
        }
        setHoistedItem(undefined);
      }
      const oldIndex = newPinnedItems.findIndex((item) => {
        return item.data.node.id === id;
      });
      if (oldIndex === -1) {
        return;
      }
      const newOrder = arrayMove(newPinnedItems, oldIndex, index);
      setPinnedItems(newOrder);
    },
    [hoistedItem, pinnedItems]
  );
  /**
   * Effect that updates items in state based on parsed GQL data
   * wrapping each one in an AppBarItem prior to insertion
   */
  React.useEffect(() => {
    if (!allApps) {
      return;
    }
    const edgeToItemArray = (
      items: M365ApplicationEdge[] | undefined
    ): AppBarItem[] => {
      if (!items) {
        return [];
      }
      return items.map(wrapNewItem);
    };
    setAllAppBarItems(edgeToItemArray(allApps));
    setLockedItems(edgeToItemArray(lockedTiles));
    setPinnedItems(edgeToItemArray(pinnedTiles));
    setOverflowItems(edgeToItemArray(overflowTiles));
    setAnchoredItems(edgeToItemArray(anchoredTiles));
  }, [overflowTiles, lockedTiles, pinnedTiles, anchoredTiles, allApps]);
  return (
    <AppBarContext.Provider
      value={{
        appBarData,
        overflowIndex,
        maxLockedIndex,
        maxPinnedIndex,
        setOverflowIndex,
        lockedItems,
        setLockedItems,
        pinnedItems,
        setPinnedItems,
        movePinnedItem,
        hoistedItem,
        setHoistedItem,
        overflowItems,
        selectedItem,
        anchoredItems,
        setAnchoredItems,
        allAppBarItems,
      }}
    >
      {children}
    </AppBarContext.Provider>
  );
};
export { AppBarContext, AppBarContextProvider };
